import DefaultValuesUtils from "./DefaultValuesUtils";
import TemplateCustom from "../../components/Templates/TemplateCustom";
import TemplateCustomMinified from "../../components/Templates/TemplateCustomMinified";
import TemplateDefault from "../../components/Templates/TemplateDefault";

/**
 *  ***********
 *  ** Types **
 *  ***********
 */

/**
 * @typedef {Object} TemplateData
 * @description Corresponds to the data extracted from MessageDataJson.
 * 
 * @property {string} title - The template's header.
 * @property {string} body - The template's content.
 * @property {string} footer - The template's footer.
 * @property {Buttons[]} buttons - The template's buttons attached.
 * @property {SubMessage} subMessage - The template's sub message: Document, Image, Location or Video.
 */

/**
 * @typedef SubMessage
 * @property {string} type - The sub message type: 'document', 'image', 'location' or 'video'.
 * @property {string} mediaUrl - The url of the media attached to the template message.
 * @property {string} body - The location attached to the template message.
 */

/**
 * @typedef {CallButton | UrlButton | QuickReplyButton | CtaUrlButton} Button
 */

/**
 * @typedef {Object} CallButton
 * @property {string} displayText - The button's label.
 * @property {string} phoneNumber - The phone number to call.
 */

/**
 * @typedef {Object} UrlButton
 * @property {string} displayText - The button's label.
 * @property {string} url - The anchor url.
 * @property {string} consentedUserUrl - The consentment url.
 * @property {string} webviewPresentation - The url preview.
 */

/**
 * @typedef {Object} QuickReplyButton
 * @property {number} id - The button's id to be sent as response.
 * @property {string} displayText - The button's label.
 */

/**
 * @typedef {Object} CtaUrlButton
 * @property {string} displayText - The button's label.
 * @property {string} url - The anchor url.
 * @property {string} webviewPresentation - The url preview.
 * @property {boolean} paymentLinkPreview - The flag to show/hide link preview for payments.
 * @property {string} landingPageUrl - The company landing page url.
 * @property {boolean} webviewInteraction - The url preview.
 */

/**
 * @typedef TemplateMessage
 * @description - When a property is wrapped by square brackets, it means the property is optional.
 * 
 * @property {Object} [fourRowTemplate] - A template's type.
 * @property {Object} [hydratedFourRowTemplate] - A template's type.
 * @property {Object} [hydratedTemplate] - A template's type.
 * @property {Object} [interactiveMessageTemplate] - A template's type.
 */

////////////////////

/**
 *  ***********
 *  ** Class **
 *  ***********
 */

/**
 * Class responsible for handling and rendering template messages.
 * 
 * @class TemplateMessageUtils
 * @type {Object} Message - Corresponds to the message object from Message Model received from the back-end.
 * @type {Object} MessageDataJson - Corresponds to the message json object returned by WhatsApp and assigned to Message Model.
 * @type {Object} TemplateMessage - Corresponds to the template message json object returned by WhatsApp and assigned to MessageDataJson.
 * @type {Object} proto.ITemplateButton - Corresponds to the buttons from the 'MessageDataJson' (type: WAMessage).
 * @type {Object} TemplateData - Corresponds to the data extracted from MessageDataJson.
 * @example
 * const templateComponent = TemplateMessageUtils.renderTemplate(message);
 * return ({templateComponent}); // React.Component
 * 
 */
class TemplateMessageUtils {
  //  ****************
  //  ** Attributes **
  //  ****************
  static DEFAULT_TEMPLATE_DATA = Object.freeze({
    title: ""
    , body: ""
    , footer: ""
    , buttons: []
    , subMessage: { type: "", mediaUrl: "", body: "" }
  });



  //  ***********************
  //  ** Methods - Private **
  //  ***********************

  /**
   * 
   * @param {Message} message - The message object from Message Model to be rendered as the default template.
   * @returns {React.Component} The default template component.
   */
  static #renderDefaultTemplate(message) {
    return (<TemplateDefault message={message} />);
  }

  /**
   * 
   * @param {Message} message - The message object from Message Model to be rendered as a template.
   * @param {WAMessage} messageDataJson - The message object JSON from Baileys/WhatsApp.
   * @param {string valueof DefaultValuesUtils.TEMPLATE_TYPES} templateType - The type of the template to be rendered.
   * @returns {React.Component} - The respective template type component.
   */
  static #renderCustomTemplate(message, ticketId, messageDataJson, templateType) {
    // ***---- fourRowTemplate ----***
    if (templateType === DefaultValuesUtils.TEMPLATE_TYPES.fourRowTemplate) return (<TemplateDefault message={message} />);

    // ***---- hydratedFourRowTemplate, hydratedTemplate && interactiveMessageTemplate ----***
    return (<TemplateCustom message={message} ticketId={ticketId} messageDataJson={messageDataJson} templateType={templateType} />);
  }

  /**
   * 
   * @param {Message} message - The message object from Message Model to be rendered as a template.
   * @param {WAMessage} messageDataJson - The message object JSON from Baileys/WhatsApp.
   * @param {string valueof DefaultValuesUtils.TEMPLATE_TYPES} templateType - The type of the template to be rendered.
   * @param {boolean} shouldDisplayCompleteTemplate - Whether the minified template should be displayed with mediaUrl, buttons and footer.
   * @returns {React.Component} - The respective template type component.
   */
  static #renderCustomTemplateMinified(message, messageDataJson, templateType, shouldDisplayCompleteTemplate) {
    return (
      <TemplateCustomMinified
        message={message}
        messageDataJson={messageDataJson}
        templateType={templateType}
        shouldDisplayCompleteTemplate={shouldDisplayCompleteTemplate}
      />
    );
  }

  /**
   * 
   * @param {WAMessage} messageDataJson - The message object JSON from Baileys/WhatsApp.
   * @returns {string valueof <DefaultValuesUtils.TEMPLATE_TYPES | DefaultValuesUtils.DEFAULT_TEMPLATE_TYPES>} - The type of the template.
   */
  static #getTemplateType(messageDataJson) {
    const templateMessage = messageDataJson.message?.templateMessage;

    if (templateMessage?.fourRowTemplate) return DefaultValuesUtils.TEMPLATE_TYPES.fourRowTemplate;
    if (templateMessage?.hydratedFourRowTemplate) return DefaultValuesUtils.TEMPLATE_TYPES.hydratedFourRowTemplate;
    if (templateMessage?.hydratedTemplate) return DefaultValuesUtils.TEMPLATE_TYPES.hydratedTemplate;
    if (templateMessage?.interactiveMessageTemplate) return DefaultValuesUtils.TEMPLATE_TYPES.interactiveMessageTemplate;

    return DefaultValuesUtils.DEFAULT_TEMPLATE_TYPES.defaultTemplate;
  }

  /**
   * 
   * @param {proto.ITemplateButton} buttons - Corresponds to the buttons from the 'MessageDataJson' (type: WAMessage).
   * @returns {Button[]} - The extracted template buttons.
   */
  static #getTemplateButtons(buttons) {
    const buttonsArray = [];

    for (const button of buttons || []) {
      if (!button.callButton && !button.urlButton && !button.quickReplyButton) continue;

      const buttonData = { index: button.index ?? -1 };

      if (button.callButton) {
        buttonData.callButton = {
          displayText: button.callButton.displayText ?? ""
          , phoneNumber: button.callButton.phoneNumber ?? ""
        };
      }
      else if (button.urlButton) {
        buttonData.urlButton = {
          consentedUserUrl: button.urlButton.consentedUserUrl ?? ""
          , displayText: button.urlButton.displayText ?? ""
          , url: button.urlButton.url ?? ""
          , webviewPresentation: button.urlButton.webviewPresentation ?? ""
        };
      }
      else {
        buttonData.quickReplyButton = {
          id: button.quickReplyButton.id ?? -1
          , displayText: button.quickReplyButton.displayText ?? ""
        };
      }

      buttonsArray.push(buttonData);
    }

    return buttonsArray;
  }

  /**
   * 
   * @param {proto.ITemplateButton} buttons - Corresponds to the buttons from the 'MessageDataJson' (type: WAMessage).
   * @param {string} paramsJson - The buttons parameters in JSON stringified format.
   * @returns {Button[]} - The extracted template buttons.
   */
  static #getTemplateInteractiveButtons(buttons, paramsJson) {
    const messageParamsJson = paramsJson ? JSON.parse(paramsJson).tap_target_list ?? [] : [];
    const buttonsArray = [];

    for (const [index, button] of (buttons || []).entries()) {
      if (button.name !== "cta_url") continue;

      const buttonParamsJson = button.buttonParamsJson ? JSON.parse(button.buttonParamsJson) : {};
      const buttonData = { index: messageParamsJson[index]?.button_index ?? -1 };

      buttonData.ctaUrlButton = {
        displayText: buttonParamsJson.display_text ?? ""
        , url: buttonParamsJson.url ?? ""
        , webviewPresentation: buttonParamsJson.webview_presentation ?? ""
        , paymentLinkPreview: buttonParamsJson.payment_link_preview ?? false
        , landingPageUrl: buttonParamsJson.landing_page_url ?? ""
        , webviewInteraction: buttonParamsJson.webview_interaction ?? false
      };

      buttonsArray.push(buttonData);
    }

    return buttonsArray;
  }

  /**
   * 
   * @param {*} templateMessage - The template message object JSON from Baileys/WhatsApp.
   * @returns {string} - The sub message type: 'document', 'image', 'location' or 'video'.
   */
  static #getSubMessageType(templateMessage) {
    if (templateMessage.documentMessage) return DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.document;
    if (templateMessage.imageMessage) return DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.image;
    if (templateMessage.locationMessage) return DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.location;
    if (templateMessage.videoMessage) return DefaultValuesUtils.MESSAGE_TYPES.convertedTypes.video;
    return "";
  }
  
  /**
   * 
   * @param {Message} message - The message object from Message Modal.
   * @param {TemplateMessage} templateMessage - The template message object JSON from Baileys/WhatsApp.
   * @param {boolean} shouldUseDefaultSubMessage - Whether or not to use the default empty sub message.
   * @returns {SubMessage} - The template's sub message: Document, Image, Location or Video.
   */
  static #getSubMessage(message, templateMessage, shouldUseDefaultSubMessage) {
    if (shouldUseDefaultSubMessage) return { type: "", mediaUrl: "", body: "" };
    return { type: this.#getSubMessageType(templateMessage), mediaUrl: message.mediaUrl ?? "", body: message.body ?? "" };
  }

  /**
   * 
   * @param {Message} message = The message object from Message Model.
   * @param {WAMessage} messageDataJson - The message object JSON from Baileys/WhatsApp.
   * @returns {TemplateData} - The extracted template data.
   */
  static #getHydratedTemplateData(message, messageDataJson) {
    const hydratedTemplate = messageDataJson.message.templateMessage.hydratedTemplate;

    const title = hydratedTemplate?.hydratedTitleText ?? "";
    const body = hydratedTemplate?.hydratedContentText ?? "";
    const footer = hydratedTemplate?.hydratedFooterText ?? "";
    const buttons = this.#getTemplateButtons(hydratedTemplate?.hydratedButtons);
    const subMessage = this.#getSubMessage(message, hydratedTemplate, false);

    return { title, body, footer, buttons, subMessage };
  }

  /**
   * 
   * @param {Message} message = The message object from Message Model.
   * @param {WAMessage} messageDataJson - The message object JSON from Baileys/WhatsApp.
   * @returns {TemplateData} - The extracted template data.
   */
  static #getHydratedFourRowTemplateData(message, messageDataJson) {
    const hydratedFourRowTemplate = messageDataJson.message.templateMessage.hydratedFourRowTemplate;

    const title = hydratedFourRowTemplate?.hydratedTitleText ?? "";
    const body = hydratedFourRowTemplate?.hydratedContentText ?? "";
    const footer = hydratedFourRowTemplate?.hydratedFooterText ?? "";
    const buttons = this.#getTemplateButtons(hydratedFourRowTemplate?.hydratedButtons);
    const subMessage = this.#getSubMessage(message, hydratedFourRowTemplate, false);

    return { title, body, footer, buttons, subMessage };
  }

  /**
   * 
   * @param {Message} message = The message object from Message Model.
   * @param {WAMessage} messageDataJson - The message object JSON from Baileys/WhatsApp.
   * @returns {TemplateData} - The extracted template data.
   */
  static #getInteractiveTemplateData(message, messageDataJson) {
    const interactiveTemplateMessage = messageDataJson.message?.templateMessage?.interactiveMessageTemplate;

    const title = interactiveTemplateMessage.header?.text ?? "";
    const body = interactiveTemplateMessage.body?.text ?? "";
    const footer = interactiveTemplateMessage.footer?.text ?? "";
    const buttons = this.#getTemplateInteractiveButtons(interactiveTemplateMessage.nativeFlowMessage?.buttons, interactiveTemplateMessage.nativeFlowMessage?.messageParamsJson);
    const subMessage = this.#getSubMessage(message, interactiveTemplateMessage, true);

    return { title, body, footer, buttons, subMessage };
  }



  //  **********************
  //  ** Methods - Public **
  //  **********************

  /**
   * 
   * @param {Message} message - The message object from Message Model.
   * @returns {string valueof <DefaultValuesUtils.DEFAULT_TEMPLATE_TYPES | DefaultValuesUtils.TEMPLATE_TYPES>} - The template's type.
   */
  static getTemplateTypeByMessage(message) {
    if (!message.dataJson || Object.keys(message.dataJson).length === 0) return DefaultValuesUtils.DEFAULT_TEMPLATE_TYPES.defaultTemplate;
    
    const messageDataJson = JSON.parse(message.dataJson);
    return this.#getTemplateType(messageDataJson);
  }

  /**
   * 
   * @param {Message} message - The message object from Message Model.
   * @param {WAMessage} messageDataJson - The message object JSON from Baileys/WhatsApp.
   * @param {string} templateType - The template's type.
   * @returns {TemplateData} - The extracted template data.
   */
  static getTemplateData(message, messageDataJson, templateType) {
    if (!messageDataJson || Object.keys(messageDataJson).length === 0) return this.DEFAULT_TEMPLATE_DATA;

    if (templateType === DefaultValuesUtils.TEMPLATE_TYPES.fourRowTemplate) {
      return this.DEFAULT_TEMPLATE_DATA;
    }

    if (templateType === DefaultValuesUtils.TEMPLATE_TYPES.hydratedFourRowTemplate) {
      return this.#getHydratedFourRowTemplateData(message, messageDataJson);
    }

    if (templateType === DefaultValuesUtils.TEMPLATE_TYPES.hydratedTemplate) {
      return this.#getHydratedTemplateData(message, messageDataJson);
    }

    return this.#getInteractiveTemplateData(message, messageDataJson);
  }

  /**
   * 
   * @param {Message} message  - The message object from Message Model to be rendered as a template.
   * @param {number | null} ticketId - The ticket ID associated to the template's interactive button.
   * @param {boolean} shouldDisplayCompleteTemplate - Whether the minified template should be displayed with mediaUrl, buttons and footer.
   * @returns {React.Component} - The template component.
   */
  static renderTemplate(message, ticketId = null, shouldDisplayCompleteTemplate = false) {
    try {
      if (!message.dataJson) return this.#renderDefaultTemplate(message);

      const messageDataJson = JSON.parse(message.dataJson);
      const templateType = this.#getTemplateType(messageDataJson);

      if (Object.keys(DefaultValuesUtils.DEFAULT_TEMPLATE_TYPES).includes(templateType)) return this.#renderDefaultTemplate(message);
      else if (ticketId) return this.#renderCustomTemplate(message, ticketId, messageDataJson, templateType);
      else return this.#renderCustomTemplateMinified(message, messageDataJson, templateType, shouldDisplayCompleteTemplate);
    }
    catch (exception) {
      console.log("- Template Message Utils - renderTemplate exception:", exception);
      return this.#renderDefaultTemplate(message);
    }
  }
};

export default TemplateMessageUtils;
